<template>
  <AppLayout>
    <div class="flex flex-col items-center gap-4">
      <h1>Transactions</h1>

      <div class="flex gap-4 w-full max-w-4xl">
        <div class="flex-1 max-w-xs">
          <label for="type-select" class="block text-sm font-medium mb-2">Transaction type</label>
          <MultiSelect
            id="type-select"
            v-model="selectedTypes"
            :options="transactionTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="All types"
            class="w-full"
            display="chip"
          />
        </div>

        <div class="flex-1">
          <label for="org-select" class="block text-sm font-medium mb-2">Organization</label>
          <Select
            id="org-select"
            v-model="selectedOrgId"
            :options="organizationOptions"
            optionLabel="name"
            optionValue="id"
            placeholder="All organizations"
            class="w-full"
            filter
          >
            <template #option="{ option }">
              <div>
                {{ option.name }}
                <span v-if="option.code" class="text-surface-400 ml-2">{{ option.code }}</span>
              </div>
            </template>
          </Select>
        </div>

        <div class="flex-1">
          <label for="email-filter" class="block text-sm font-medium mb-2">User email</label>
          <InputText
            id="email-filter"
            v-model="emailFilter"
            placeholder="Filter by email..."
            class="w-full"
          />
        </div>
      </div>

      <DataTable
        :value="transactions"
        :loading="loading"
        paginator
        :rows="50"
        :rowsPerPageOptions="[50, 100, 200]"
        :totalRecords="totalRecords"
        :lazy="true"
        @page="onPage"
        tableStyle="min-width: 50rem"
        :rowClass="(data: AdminTransactionDto) => (isExpired(data.expires) ? 'opacity-60' : '')"
      >
        <Column field="type" header="Type" />
        <Column header="User">
          <template #body="{ data }">
            <span v-if="data.userEmail">{{ data.userEmail }}</span>
            <span v-else class="text-surface-400 italic">(anonymous)</span>
          </template>
        </Column>
        <Column header="Organization">
          <template #body="{ data }">
            <span v-if="data.userEmail">{{ data.orgName }}</span>
            <span v-else class="text-surface-400 italic">{{ data.orgName }}</span>
          </template>
        </Column>
        <Column field="credits" header="Tokens" />
        <Column field="used" header="Used" />
        <Column header="Expiration">
          <template #body="{ data }">
            <span v-if="data.expires" class="inline-flex items-center gap-2">
              {{ formatDate(data.expires) }}
              <Tag
                :value="isExpired(data.expires) ? 'Expired' : 'Active'"
                :severity="isExpired(data.expires) ? 'secondary' : 'success'"
              />
            </span>
            <span v-else>—</span>
          </template>
        </Column>
        <Column header="Created">
          <template #body="{ data }">
            {{ formatDate(data.created) }}
          </template>
        </Column>
      </DataTable>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import dayjs from 'dayjs';
import type {
  AdminTransactionControllerListData,
  AdminTransactionDto,
  OrganizationDto,
} from '@/api/generated';
import AppLayout from '@/components/AppLayout.vue';
import { useApiClient } from '@/api/apiClient';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import Tag from 'primevue/tag';

const { api } = useApiClient();

const transactions = ref<AdminTransactionDto[]>([]);
const loading = ref(false);
const totalRecords = ref(0);
const currentOffset = ref(0);
const currentLimit = ref(50);

const selectedOrgId = ref<string>('');
type TransactionTypeValue = NonNullable<
  NonNullable<AdminTransactionControllerListData['query']>['types']
>[number];
const selectedTypes = ref<TransactionTypeValue[]>([]);
const emailFilter = ref('');
const organizations = ref<OrganizationDto[]>([]);

const transactionTypeOptions = [
  { label: 'Free Credits', value: 'FreeCredits' },
  { label: 'Auto-Renewable Subscription', value: 'Auto-Renewable Subscription' },
  { label: 'Consumable', value: 'Consumable' },
  { label: 'Redeemed Credits', value: 'RedeemedCredits' },
  { label: 'Stripe Payment Link', value: 'StripePaymentLink' },
  { label: 'Stripe Checkout', value: 'StripeCheckout' },
];

const organizationOptions = computed(() => [
  { id: '', name: 'All organizations', code: '' },
  ...organizations.value.map(org => ({ id: org.id, name: org.name, code: org.code })),
]);

function formatDate(date: string): string {
  return dayjs(date).format('MMM D, YYYY HH:mm');
}

function isExpired(expires: string | null): boolean {
  return expires !== null && dayjs(expires).isBefore(dayjs());
}

async function loadTransactions() {
  loading.value = true;
  try {
    const { data, error } = await api.adminTransactionControllerList({
      query: {
        orgId: selectedOrgId.value || undefined,
        email: emailFilter.value || undefined,
        types: selectedTypes.value.length > 0 ? selectedTypes.value : undefined,
        limit: String(currentLimit.value),
        offset: String(currentOffset.value),
      },
    });
    if (error) {
      throw new Error('Failed to load transactions');
    }
    transactions.value = data?.data ?? [];
    totalRecords.value = data?.total ?? 0;
  } finally {
    loading.value = false;
  }
}

async function loadOrganizations() {
  const { data, error } = await api.adminOrganizationControllerList();
  if (error) {
    throw new Error('Failed to load organizations');
  }
  organizations.value = data ?? [];
}

function onPage(event: { first: number; rows: number }) {
  currentOffset.value = event.first;
  currentLimit.value = event.rows;
  loadTransactions();
}

const debouncedLoadTransactions = useDebounceFn(() => {
  currentOffset.value = 0;
  loadTransactions();
}, 300);

watch(selectedOrgId, () => {
  currentOffset.value = 0;
  loadTransactions();
});

watch(selectedTypes, () => {
  currentOffset.value = 0;
  loadTransactions();
});

watch(emailFilter, () => {
  debouncedLoadTransactions();
});

loadTransactions();
loadOrganizations();
</script>
